import { memo, useState, useCallback, useMemo } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { Row, Col, Card, Modal } from "react-bootstrap";
import capitalize from "lodash/capitalize";

import ComponentLoader from "../../ComponentLoader";
import { getCardDetailByUserId } from "../../../services/userServices";
import ErrorPage from "../../ErrorPage";
import { QueryKeyEnum } from "../../../types";
import { cardStatusLabel, HTTP_CODES } from "../../../constants";

import styles from "../../../styles/user.module.scss";
import { CardDetail, CardStatus } from "../../../types/CardDetail";
import { CardTextItem } from "..";
import { CopyButton } from "../CopyButton";
import CardTerminationForm from "../CardTerminationForm";
import { featureFlags, EnvironmentNames } from "../../../featureFlags";

export interface CardDetailProps {
  userId: string;
}

export function UserCardDetail(props: CardDetailProps) {
  const { isLoading, error, data } = useQuery<CardDetail, Error>(
    [QueryKeyEnum.GetCardDetail, props.userId],
    () => getCardDetailByUserId(props.userId)
  );
  const [isOpenCardTerminationModal, setOpenCardTerminationModal] =
    useState(false);

  const openCardTerminationModal = () => setOpenCardTerminationModal(true);

  const closeCardTerminationModal = useCallback(() => {
    setOpenCardTerminationModal(false);
  }, []);

  const enableTerminateCard = useMemo(() => {
    const stage = process.env.REACT_APP_STAGE as EnvironmentNames;
    return featureFlags[stage]?.enableTerminateCard;
  }, []);

  if (isLoading) return <ComponentLoader isLoading={isLoading} />;

  if (
    axios.isAxiosError(error) &&
    error.response?.status === HTTP_CODES.NOT_FOUND
  ) {
    return (
      <Card.Body className={styles.cardTextItem}>
        Digital card not activated yet
      </Card.Body>
    );
  }

  if (error || !data) return <ErrorPage />;

  return (
    <>
      <Card.Body className={styles.cardText}>
        <Row>
          <Col>
            <CardTextItem
              topic="Cardholder name"
              content={data.cardholderName}
            />
            <div className={styles.cardStatusAndNumber}>
              <div className={styles.cardStatus}>
                <CardTextItem
                  topic="Card status"
                  content={capitalize(cardStatusLabel[data.status])}
                  dataDisable={data.status === CardStatus.PermBlock}
                />
                {data.status !== CardStatus.PermBlock && enableTerminateCard && (
                  <b
                    className={styles.openCardTerminationModalButton}
                    onClick={openCardTerminationModal}
                  >
                    Terminate card
                  </b>
                )}
              </div>
              <div className={styles.separator}>|</div>
              <div className={styles.copyToClipboard}>
                <CardTextItem
                  topic="Last 4 digits"
                  content={data.cardNumberLastDigits}
                />
                <CopyButton text={data.cardNumberLastDigits!} />
              </div>
            </div>
            <CardTextItem
              topic="Fazz card opaque ID"
              content={data.cardOpaqueId}
            />
            <CardTextItem
              topic="Print request date"
              content={data.printRequestDate || "No physical card"}
            />
            <CardTextItem
              topic="Delivery address"
              content={data.deliveryAddress || "-"}
            />
          </Col>
        </Row>
      </Card.Body>
      <Modal
        centered
        show={isOpenCardTerminationModal}
        onHide={closeCardTerminationModal}
        dialogClassName={styles.cardTerminationModal}
      >
        <CardTerminationForm
          userCardData={data}
          onCancel={closeCardTerminationModal}
        />
      </Modal>
    </>
  );
}

export default memo(UserCardDetail);
